// Libraries
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Components
import SmartSelector from "../../InputComponents/SmartSelector";
import { LabeledFormInput } from "../LabeledFormInput";

// Interfaces
import { ISmartSelectorProps } from "../../InputComponents/SmartSelector/interfaces";

function isValidCity(value: ICity | null) {
	return Boolean(value?.name && value?.countryCode);
}

async function getCitiesByStateAndCountry(countryCode?: string, stateCode?: string, partialName?: string) {
	if (!countryCode || !stateCode) {
		return [];
	}

	const query = gql`
		query GetCities($countryCode: String!, $stateCode: String!, $partialName: String) {
			getCities(countryCode: $countryCode, stateCode: $stateCode, partialName: $partialName) {
				id
				name
				stateCode
				stateName
				countryCode
				countryName
			}
		}
	`;

	const variables = { countryCode, stateCode, partialName };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getCities ?? [];
	} catch (error) {
		throw new Error("Failed to fetch cities");
	}
}

type TCitySelectorProps = {
	countryCode?: string;
	stateCode?: string;
	onCitySelectedAction?: (city: ICity | null) => void;
	inferredCity?: ICity | null;
	selectorProps?: Partial<ISmartSelectorProps>;
	required?: boolean;
	disabled: boolean;
	loading: boolean;
};

export function CitySelector(props: Readonly<TCitySelectorProps>) {
	const { countryCode, stateCode, onCitySelectedAction, inferredCity = null, selectorProps, required = false, disabled, loading } = props;
	const [selectedCity, setSelectedCity] = useState<ICity | null>(null);

	const {
		data: cities = [],
		isLoading: isLoadingCities,
		error: errorLoadingCities,
	} = useQuery<ICity[]>({
		queryKey: ["getCitiesByStateAndCountry", countryCode, stateCode],
		queryFn: async () => getCitiesByStateAndCountry(countryCode, stateCode),
		staleTime: Infinity,
		enabled: Boolean(countryCode) && Boolean(stateCode),
	});

	const handleCitySelection: ISmartSelectorProps["onChangeAction"] = (value) => {
		const city = value as ICity;
		setSelectedCity(city);
		onCitySelectedAction?.(city);
	};

	useEffect(() => {
		setSelectedCity((prev) => {
			if (prev?.name === inferredCity?.name) {
				return prev;
			}
			return {
				id: inferredCity?.id ?? "",
				name: inferredCity?.name ?? "",
				countryCode: inferredCity?.countryCode,
				countryName: inferredCity?.countryName,
				stateCode: inferredCity?.stateCode,
				stateName: inferredCity?.stateName,
			} as ICity;
		});
	}, [inferredCity]);

	return (
		<LabeledFormInput
			label="City"
			formInput={
				<SmartSelector
					selectBy={"name"}
					showBy={"name"}
					options={cities}
					selected={selectedCity}
					listBoxProps={{}}
					disabled={disabled || !countryCode || !stateCode}
					sx={{}}
					onChangeAction={handleCitySelection}
					loading={isLoadingCities || loading}
					error={required && (!isValidCity(selectedCity) || Boolean(errorLoadingCities))}
					clearable
					popperProps={selectorProps?.popperProps}
					inputProps={{
						placeholder: "City",
					}}
				/>
			}
		/>
	);
}

export default CitySelector;
